import type { InputEvent, InputKind } from '../engine/types';

export type InputState = 'idle' | 'starting' | 'active' | 'error' | 'unsupported' | 'lost';

/** Situation précise derrière un état, pour expliquer quoi faire (parcours de configuration). */
export type StatusCode = 'denied' | 'insecure' | 'unsupported' | 'notFound' | 'busy' | 'noDevice' | 'lost' | 'interrupted' | 'error';

export interface DeviceInfo {
  id: string;
  name: string;
}

export interface AudioProcessingInfo {
  echoCancellation?: boolean;
  noiseSuppression?: boolean;
  autoGainControl?: boolean;
  sampleRate?: number;
}

export interface InputStatus {
  kind: InputKind;
  state: InputState;
  /** Message lisible pour l'interface (état, conseil, erreur). */
  message: string;
  /** Code de la situation quand quelque chose bloque ; absent quand tout va bien. */
  code?: StatusCode;
  devices: DeviceInfo[];
  activeDevice?: string;
  /** Niveau du micro (dB) et plancher de bruit, pour le vumètre. */
  level?: { db: number; noise: number };
  /** Prétraitements audio réellement appliqués par le navigateur (micro). */
  processing?: AudioProcessingInfo;
  /** Latence d'entrée annoncée par le navigateur pour la piste micro (ms), si disponible. */
  inputLatencyMs?: number;
}

export type EventListener = (e: InputEvent) => void;
export type StatusListener = (s: InputStatus) => void;

export interface InputSource {
  readonly kind: InputKind;
  start(): Promise<void>;
  stop(): void;
  subscribe(listener: EventListener): () => void;
  onStatus(listener: StatusListener): () => void;
  getStatus(): InputStatus;
}

export abstract class BaseInputSource implements InputSource {
  readonly kind: InputKind;
  private readonly listeners = new Set<EventListener>();
  private readonly statusListeners = new Set<StatusListener>();
  protected status: InputStatus;

  constructor(kind: InputKind) {
    this.kind = kind;
    this.status = { kind, state: 'idle', message: '', devices: [] };
  }

  abstract start(): Promise<void>;
  abstract stop(): void;

  subscribe(listener: EventListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  onStatus(listener: StatusListener): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  getStatus(): InputStatus {
    return this.status;
  }

  protected emit(e: InputEvent): void {
    for (const l of this.listeners) l(e);
  }

  protected setStatus(patch: Partial<InputStatus>): void {
    this.status = { ...this.status, ...patch };
    for (const l of this.statusListeners) l(this.status);
  }
}

export function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now();
}
